import { useState, useEffect, lazy, Suspense } from 'react';
import { api } from '../App';

// Components - Critical path
import HeroSection from '../components/HeroSection';
import CarCard from '../components/CarCard';

// Lazy load below-the-fold components
const SalesIdentitySection = lazy(() => import('../components/SalesIdentitySection'));
const TechnologySection = lazy(() => import('../components/TechnologySection'));
const FAQSection = lazy(() => import('../components/FAQSection'));
const Footer = lazy(() => import('../components/Footer'));

export default function HomePage() {
  const [cars, setCars] = useState([]);
  const [salesInfo, setSalesInfo] = useState(null);
  const [settings, setSettings] = useState(() => {
    // Try to get cached settings from localStorage for instant display
    try {
      const cached = localStorage.getItem('byd_settings');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(!settings); // Not loading if we have cached data

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [carsRes, salesRes, settingsRes, pageRes] = await Promise.all([
          api.get('/cars'),
          api.get('/sales-info'),
          api.get('/admin-settings'),
          api.get('/page-content/home')
        ]);
        setCars(carsRes.data);
        setSalesInfo(salesRes.data);
        setSettings(settingsRes.data);
        setPageContent(pageRes.data);
        // Cache settings for instant display on next load
        try {
          localStorage.setItem('byd_settings', JSON.stringify(settingsRes.data));
        } catch {}
      } catch (e) {
        console.error('Error fetching data:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Separate featured/bestseller cars
  const featuredCars = cars.filter(car => car.is_featured || car.is_bestseller);
  const otherCars = cars.filter(car => !car.is_featured && !car.is_bestseller);

  // Get section content from pageContent or defaults
  const featuredLabel = pageContent?.section_featured_label || 'Unit Terlaris';
  const featuredTitle = pageContent?.section_featured_title || 'Model Unggulan Kami';
  const featuredDesc = pageContent?.section_featured_description || 'BYD M6 dan Atto 1 menjadi pilihan favorit keluarga Indonesia dengan kenyamanan, efisiensi, dan biaya operasional rendah.';
  
  const allLabel = pageContent?.section_all_label || 'Lineup Lengkap';
  const allTitle = pageContent?.section_all_title || 'Jelajahi Semua Model BYD';
  const allDesc = pageContent?.section_all_description || 'Dari hatchback kompak hingga MPV mewah, temukan mobil listrik BYD yang sesuai dengan kebutuhan Anda.';

  return (
    <main data-testid="home-page">
      {/* Hero Section */}
      <HeroSection pageContent={pageContent} settings={settings} isLoading={loading} />

      {/* Featured Models - M6 & Atto 1 */}
      <section className="section-spacing" data-testid="featured-cars-section">
        <div className="byd-container">
          <div className="text-center mb-12 animate-fadeInUp">
            <span className="text-label mb-4 block">{featuredLabel}</span>
            <h2 className="heading-display text-3xl sm:text-4xl text-slate-900 mb-4">
              {featuredTitle}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {featuredDesc}
            </p>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 gap-8">
              {[1, 2].map(i => (
                <div key={i} className="aspect-car bg-slate-100 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8">
              {featuredCars.map((car, idx) => (
                <CarCard key={car.id} car={car} index={idx} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* All Models Grid */}
      <section className="section-spacing bg-slate-50" data-testid="all-cars-section">
        <div className="byd-container">
          <div className="text-center mb-12 animate-fadeInUp">
            <span className="text-label mb-4 block">{allLabel}</span>
            <h2 className="heading-display text-3xl sm:text-4xl text-slate-900 mb-4">
              {allTitle}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {allDesc}
            </p>
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="aspect-car bg-slate-200 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {otherCars.map((car, idx) => (
                <CarCard key={car.id} car={car} index={idx} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lazy loaded sections */}
      <Suspense fallback={<div className="py-12 text-center"><div className="animate-spin w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full mx-auto" /></div>}>
        <TechnologySection settings={settings} />
        <SalesIdentitySection salesInfo={salesInfo} />
        <FAQSection carSlug="general" title="Pertanyaan Umum Seputar BYD" settings={settings} />
        <Footer settings={settings} salesInfo={salesInfo} cars={cars} />
      </Suspense>
    </main>
  );
}
